import React, { useReducer, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Send, FileCheck, Shield, AlertCircle, Building, Users, Award, Globe, FileText, CheckSquare } from 'lucide-react';
import { SectionTitle, Row, Col, FormField, Input, Checkbox, SignatureInput, FileInput } from '../../../SharedInputs';

export function SectionDeclaration({ state, dispatch, errors }) {
  const updateField = (field, value) => {
    dispatch({ type: 'UPDATE_FIELD', section: 'declaration', field, value });
  };

  const updateAttachment = (field, value) => {
    dispatch({ type: 'UPDATE_FIELD', section: 'attachments', field, value });
  };
  
  const updateDocuments = (file) => {
    dispatch({
      type: 'SET_FILES',
      section: 'declaration',
      field: 'additionalDocuments',
      file
    });
  };
  const getFieldError = (fieldName) => {
    return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };

  // Auto-set today's date if not already set
  React.useEffect(() => {
    if (!state.declaration.date) {
      const today = new Date().toISOString().split('T')[0];
      updateField('date', today);
    }
  }, []);

  return (
    <div>
      <SectionTitle icon={CheckSquare}>Declaration & Attachments</SectionTitle>
      
      {/* Important Links Section */}
      <div style={{ padding: 20, background: '#eff6ff', borderRadius: 12, border: '1px solid #bae6fd', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <AlertCircle size={24} color="#2563eb" style={{ marginTop: 4, flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: '0 0 12px', color: '#1e40af' }}>Agent Obligations</h4>
            <p style={{ fontSize: 14, color: '#1e40af', margin: '0 0 12px' }}>
              Before making an application, you should be familiar with the following:
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#1e40af', fontSize: 14, lineHeight: 1.8 }}>
              <li>
                <a 
                  href="https://www.legislation.gov.au/C2004A00757/latest/text" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  Education Services for Overseas Students Act 2000
                </a>
              </li>
              <li>
                <a 
                  href="https://www.legislation.gov.au/F2017L01182/latest/text" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  National Code of Practice for Providers of Education and Training to Overseas Students 2018
                </a>
              </li>
              <li>
                <a 
                  href="https://www.legislation.gov.au/F1998B00050/2018-01-01/text" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  Migration Agents Regulations 1998
                </a>
              </li>
              <li>
                <a 
                  href="https://internationaleducation.gov.au/News/Latest-News/Documents/Australian%20International%20Education%20and%20Training%20-%20Agent%20Code%20of%20Ethics.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  Australian International Education and Training - Agent Code of Ethics
                </a>
              </li>
            </ul>
            <p style={{ fontSize: 14, color: '#1e40af', margin: '16px 0 0' }}>
              It is also recommended that you visit:
            </p>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#1e40af', fontSize: 14, lineHeight: 1.8 }}>
              <li>
                <a 
                  href="https://www.homeaffairs.gov.au/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  Department of Home Affairs
                </a>
              </li>
              <li>
                <a 
                  href="https://www.education.gov.au/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  Department of Education and Training
                </a>
              </li>
              <li>
                <a 
                  href="https://www.studyaustralia.gov.au/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  Study in Australia
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <div style={{ padding: 20, background: '#f8faff', borderRadius: 12, border: '1px solid #e0e7ff', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Shield size={24} color="#667eea" style={{ marginTop: 4, flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: '0 0 8px', color: '#374151' }}>Privacy Notice</h4>
            <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
              Canberra Institute of Education and Technology is required to collect education agents' personal information. 
              This may be shared with the Australian Government as required for the purposes of:
            </p>
            <ul style={{ margin: '12px 0 0', paddingLeft: 20, color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
              <li>promoting compliance with the ESOS Act and the National Code;</li>
              <li>assisting with the regulation of Agents;</li>
              <li>promoting compliance with the conditions of a particular student visa or visas, or of student visas generally; or</li>
              <li>facilitating the monitoring and control of immigration.</li>
            </ul>
            <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '12px 0 0' }}>
              Information about education agents can also be accessed by all registered providers through PRISMS and includes:
            </p>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
              <li>the outcome of the enrolments</li>
              <li>the percentage of completed CoEs by the education agent</li>
              <li>the number of CoEs created with the education agent's involvement against the total number of CoEs created for the provider.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Document Upload */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ margin: '0', fontSize: 18, color: '#374151' }}>
          Business Documents Upload
        </h3>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          Upload business regesteration or any supporting document for the application. <br/>
          All documents should be in high quality scans.
        </p>

        <FormField required
          error={getFieldError("additionalDocuments")}>
          <FileInput
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(files) => {
              dispatch({
                type: 'SET_FILES',
                section: 'declaration', // use 'declaration' to separate from transcripts
                field: 'additionalDocuments',
                files: Array.from(files),
              });
            }}
            value={state.declaration.additionalDocuments}
            placeholder="Upload documents (PDF or images)"
            maxSize={15 * 1024 * 1024} // 15MB
            required
            error={getFieldError("additionalDocuments")}
          />
        </FormField>
      </div>

      {/* Required Attachments Checklist */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <FileCheck size={24} color="#374151" />
          <h3 style={{ margin: 0, fontSize: 18, color: '#374151' }}>
            Checklist of Attachments
          </h3>
        </div>
        
        <div style={{ padding: 20, background: '#fef3c7', borderRadius: 8, border: '1px solid #fcd34d' }}>
          <p style={{ fontSize: 14, color: '#92400e', margin: '0 0 16px', fontWeight: 500 }}>
            Please confirm that you have prepared the following required documents:
          </p>
          
          <Checkbox
            checked={state.attachments.hasBusinessRegistration}
            onChange={(e) => updateAttachment('hasBusinessRegistration', e.target.checked)}
            error={errors.find(e => e.includes('business registration'))}
          >
            <strong>Proof of business registration</strong> (certified copy of your business registration document from your country)
          </Checkbox>
          
          <Checkbox
            checked={state.attachments.hasCertificate}
            onChange={(e) => updateAttachment('hasCertificate', e.target.checked)}
            error={errors.find(e => e.includes('certificate'))}
          >
            <strong>Copy of MARA (Migration Agent Registration Authority) Certificate</strong> OR <strong>Qualified Education Agent Counsellor Certificate from PIER</strong> (Professional International Education Resources) OR <strong>ISANA National Code Tutorial Certificate</strong>
          </Checkbox>

          <div style={{ marginTop: 16, padding: 12, background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
            <p style={{ fontSize: 13, color: '#b91c1c', margin: 0, lineHeight: 1.5 }}>
              <strong>Important:</strong> All documents in another language must be officially translated into English by a notary public or Justice of the Peace in your country. Incomplete applications or those missing required documents may not be processed.
            </p>
          </div>
        </div>
      </div>

      {/* How to Apply Instructions */}
      <div style={{ padding: 20, background: '#f0fdf4', borderRadius: 12, border: '1px solid #86efac', marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 12px', color: '#166534' }}>How to Apply</h4>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#166534', fontSize: 14, lineHeight: 1.8 }}>
          <li>All responses in this application form must be completed in English.</li>
          <li>If your documents are in another language, they must be officially translated into English by a notary public or Justice of the Peace in your country.</li>
          <li>Provide proof of business registration in your country (must be a certified copy).</li>
          <li>Submit the completed application form along with all supporting documents online, or by post or email to: <strong>Ground Floor, 15 Barry Dr, Turner ACT 2612, Australia</strong></li>
          <li>Incomplete applications or those missing required documents may not be processed.</li>
        </ul>
      </div>

      {/* Education Agent Declaration */}
      <Row>
        <Col span={12}>
          <FormField 
            label="Education Agent Declaration" 
            required
            error={errors.find(e => e.includes('declaration'))}
          >
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={state.declaration.informationTrue}
                onChange={(e) => updateField('informationTrue', e.target.checked)}
                error={!!errors.find(e => e.includes('information is true'))}
              >
                <strong>I declare that the information provided is true and correct.</strong>
              </Checkbox>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={state.declaration.refereesProvided}
                onChange={(e) => updateField('refereesProvided', e.target.checked)}
                error={!!errors.find(e => e.includes('referees are provided'))}
              >
                <strong>I declare that all referees from the industry have been provided.</strong>
              </Checkbox>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={state.declaration.proofAttached}
                onChange={(e) => updateField('proofAttached', e.target.checked)}
                error={!!errors.find(e => e.includes('proof is attached'))}
              >
                <strong>I have attached the proof of business registration.</strong>
              </Checkbox>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={state.declaration.agreeToTerms}
                onChange={(e) => updateField('agreeToTerms', e.target.checked)}
                error={!!errors.find(e => e.includes('agree to terms'))}
              >
                <strong>I am prepared to enter into an Agent Agreement with Canberra Institute of Education and Technology (CIET)</strong> in accordance with the terms and conditions outlined in the International Education Agent Agreement.
              </Checkbox>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={state.declaration.agreeToPrivacy}
                onChange={(e) => updateField('agreeToPrivacy', e.target.checked)}
                error={!!errors.find(e => e.includes('agree to privacy'))}
              >
                <strong>I agree to the collection, use and disclosure of my personal information</strong> as per the Privacy Notice.
              </Checkbox>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={state.declaration.understandResponsibilities}
                onChange={(e) => updateField('understandResponsibilities', e.target.checked)}
                error={!!errors.find(e => e.includes('confirm understanding'))}
              >
                <strong>I understand my responsibilities as an education agent</strong> under the National Code 2018 Standard 4 and have a good understanding of the international education system in Australia, including the International Education and Training Code of Ethics.
              </Checkbox>
            </div>
          </FormField>
        </Col>
      </Row>

      {/* Signature Fields */}
      <Row>
        <Col span={6}>
          <FormField 
            label="Name of Education Agent Representative" 
            required
            error={errors.find(e => e.includes('Representative name'))}
          >
            <Input
              value={state.declaration.representativeName}
              onChange={(e) => updateField('representativeName', e.target.value)}
              placeholder="Full name of representative"
            />
          </FormField>
        </Col>
        <Col span={6}>
          <FormField 
            label="Education Agent Representative Position" 
            required 
            error={errors.find(e => e.includes('Representative position'))}
          >
            <Input
              value={state.declaration.representativePosition}
              onChange={(e) => updateField('representativePosition', e.target.value)}
              placeholder="Position/Title"
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={5}>
          <FormField 
              label="Representative Signature" 
              required 
              error={errors.find(e => e.includes('Representative Signature'))}
          >
            <SignatureInput
              value={state.declaration.representativeSignature}
              onChange={(val) => updateField('representativeSignature', val || "")}
            />
          </FormField>
        </Col>
        <Col span={6}>
          <FormField 
            label="Date" 
            required 
            error={errors.find(e => e.includes('Date is required'))}
          >
            <Input
              type="date"
              value={state.declaration.date}
              onChange={(e) => updateField('date', e.target.value)}
            />
          </FormField>
        </Col>
      </Row>

      {/* Final Submit Instructions */}
      <div style={{ marginTop: 32, padding: 20, background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: 12, border: '1px solid #bae6fd' }}>
        <h4 style={{ margin: '0 0 12px', color: '#0c4a6e' }}>Ready to Submit?</h4>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#0369a1', fontSize: 14, lineHeight: 1.8 }}>
          <li>Review all sections for accuracy and completeness</li>
          <li>Ensure all required fields are completed</li>
          <li>Confirm all required documents are prepared</li>
          <li>Click "Submit Application" to complete your agent application</li>
        </ul>
        <p style={{ fontSize: 13, color: '#0369a1', margin: '16px 0 0', fontStyle: 'italic' }}>
          Once submitted, you will receive an acknowledgment within 3 working days. We will assess your application within 10 working days and contact you regarding the outcome.
        </p>
      </div>
    </div>
  );
}